import { IBetContext, TBet } from 'contexts/BetContext'
import {
    BETS_CONFIG,
    FORMULA_KEYS_MAPPER,
    TBetAffiliate,
    TBetType,
    TFormulaKey
} from 'models'
import {
    combinations,
    permutations,
    permutationsPerCombinations,
    removeDuplicates
} from 'utils'

export const parseBetDesignation = (
    designation?: string
): {
    base: Array<string>
    associates: Array<string>
} => {
    if (!designation) return { base: [], associates: [] }

    if (designation.includes('/')) {
        // 1-X/?
        const [base, associates] = designation.split('/')
        if (designation.includes('Champ total')) {
            return {
                base: base.split('-'),
                associates: []
            }
        }
        return {
            base: base.split('-'),
            associates: associates.split('-')
        }
    }

    return {
        base: designation.split('-'),
        associates: []
    }
}

export const getBetDesignation = ({
    base,
    associates,
    formulaKey = 'UNIT',
    betType
}: {
    base: string[]
    associates: string[]
    betType: TBetType
    formulaKey?: TFormulaKey
}): string => {
    let formattedBase: string[]
    const baseCount = getBetTypeBaseCount(betType)
    const rawBase = base.filter((hNum: string) => hNum !== 'X')

    if (BETS_CONFIG[betType]?.order && formulaKey.includes('WHEEL')) {
        if (base.length < baseCount)
            formattedBase = [...rawBase].concat(
                Array(baseCount - rawBase.length).fill('X')
            )
        else formattedBase = base
    } else {
        formattedBase =
            rawBase.length >= baseCount
                ? rawBase
                : [...rawBase].concat(
                      Array(baseCount - rawBase.length).fill('X')
                  )
    }

    const basesStr = formattedBase.join('-')
    const associatesStr = associates.length ? associates.join('-') : '?'

    if (formulaKey === 'WHEEL')
        return base.length ? `${basesStr}/Champ total` : ''

    if (associates.length > 0) return `${basesStr}/${associatesStr}`
    return basesStr
}

export const parseBetDesignationArray = (
    bets: TBet[],
    betType: string
): { base: string[]; associates: string[] } => {
    if (!bets.length) return { base: [], associates: [] }
    if (betType !== 'S') {
        return parseBetDesignation(bets[0].designation)
    }

    return bets
        .map((bet) => parseBetDesignation(bet.designation))
        .reduce(
            (acc, selectedRunners) => ({
                base: removeDuplicates([...acc.base, ...selectedRunners.base]),
                associates: removeDuplicates([
                    ...acc.associates,
                    ...selectedRunners.associates
                ])
            }),
            { base: [], associates: [] }
        )
}

export const getBetCombinations = (
    selection: number,
    maxElements: number,
    withPermutations: boolean = false,
    combinationLength?: number
): number => {
    if (selection < maxElements || selection === 0 || maxElements === 0)
        return 0
    if (combinationLength)
        return permutationsPerCombinations(
            selection,
            maxElements,
            combinationLength
        )
    if (withPermutations) return permutations(selection, maxElements)
    return combinations(selection, maxElements)
}

export const getBetTypeBaseCount = (betType: TBetType) => {
    return BETS_CONFIG[betType]?.baseCount || 0
}

export const betAffiliateMapper = (bet: TBet): TBetAffiliate => {
    const { betType, formulaRisk, formulaKey, operator, stake = 0 } = bet
    const { base, associates } = parseBetDesignation(bet.designation)
    const originBetTypes = BETS_CONFIG[betType]?.originBetTypes[operator]
    const originFormulaKey = FORMULA_KEYS_MAPPER[operator]?.[formulaKey]

    return {
        t: originBetTypes?.[formulaRisk === 'PLACE' ? 1 : 0] || 'UNKNOWN',
        f: originFormulaKey || 'UNKNOWN',
        fr: null,
        b: base,
        a: associates,
        tlo: bet.allOrders || false,
        mb: stake
    }
}

export const betAffiliatesMapper = (
    bets: Map<string, TBet>
): TBetAffiliate[] => {
    return Array.from(bets.values())
        .map(betAffiliateMapper)
        .filter((bet) => bet.mb > 0)
}

export const formatBetAmount = (amount: number): string => {
    const isFloat = amount % 1 !== 0
    return amount.toFixed(isFloat ? 2 : 0).replace('.', ',')
}

export const getBetCount = ({
    bets,
    selectedBets,
    selectedBetType,
    selectedFormulaKey,
    availableRunners
}: Pick<
    IBetContext,
    | 'bets'
    | 'selectedBets'
    | 'selectedBetType'
    | 'selectedFormulaKey'
    | 'availableRunners'
>) => {
    const baseCount = getBetTypeBaseCount(selectedBetType)
    const currentBets = Array.from(selectedBets.values() || []).filter(
        (bet) => bet.stake && bet.stake > 0
    )
    const { base, associates } = parseBetDesignationArray(
        currentBets,
        selectedBetType
    )
    const filteredBase = base.filter((hNum) => hNum !== 'X')
    const orderBet = BETS_CONFIG[selectedBetType]?.order
    const allOrders = currentBets[0]?.allOrders || false

    console.debug('[BETSLIP] betCount()', {
        currentBets,
        baseCount,
        filteredBase,
        associates,
        base,
        bets
    })

    if (filteredBase.length === 0) return 0

    if (selectedFormulaKey === 'UNIT' && selectedBetType !== 'S')
        return (
            getBetCombinations(filteredBase.length, baseCount, allOrders) *
            currentBets.length
        )

    if (selectedFormulaKey === 'PART_WHEEL') {
        return (
            getBetCombinations(
                associates.length,
                baseCount - filteredBase.length,
                orderBet,
                allOrders ? baseCount : undefined
            ) * currentBets.length
        )
    }

    if (selectedFormulaKey === 'WHEEL') {
        return (
            getBetCombinations(
                availableRunners.length - filteredBase.length,
                baseCount - filteredBase.length,
                orderBet,
                allOrders ? baseCount : undefined
            ) * currentBets.length
        )
    }

    return currentBets.length
}

export const getSelectedBaseCount = (designation: string) => {
    const { base } = parseBetDesignation(designation)
    return base.filter((hNum) => hNum !== 'X').length
}

export const hasSelectedAllBases = (
    designation: string,
    selectedBetType: TBetType
) =>
    getSelectedBaseCount(designation) >=
    getBetTypeBaseCount(selectedBetType) - 1
